import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@omilia/tooltip';
import { DragSource, DropTarget } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';

import _has from 'lodash/has';
import _isUndefined from 'lodash/isUndefined';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _range from 'lodash/range';
import _toString from 'lodash/toString';

import { ruleType } from 'types';
import ReorderMenu from 'components/ReorderMenu';
import Preview from './components/Preview';

import './style.scss';

/* should to use array because when we drop to rule in group we have two objects,
  rule and parent group (we need only one last object) */
let modelArr = [];
let expanded;
const getGroup = (list, item) =>
  _has(item, 'rule_group') ? _find(list, o => o.id === item.rule_group) : null;

const itemSource = {
  canDrag() {
    return true;
  },

  isDragging(props, monitor) {
    return monitor.getItem().id === props.model.id;
  },

  // called when the dragging starts
  beginDrag(props) {
    return props.model;
  },

  // called when the dragging stops
  endDrag(props, monitor) {
    if (!monitor.didDrop()) {
      return;
    }
    const model = monitor.getDropResult().model[0];
    if (props.model.id !== model.id) {
      props.moveRuleToGroup(props.model, model, expanded);
    }
    modelArr = [];
  },
};

const canMoveItem = (dropItem, dragItem, fullGroups) => {
  const group = getGroup(fullGroups, dropItem);
  if (_has(dragItem, 'rules') && _has(dropItem, 'rule_group')) {
    return !group.visible;
  } else if (_has(dragItem, 'rule_group') && _has(dropItem, 'rule_group')) {
    return !group.archived;
  }
  return true;
};

const cardTarget = {
  canDrop(props, monitor) {
    return canMoveItem(props.model, monitor.getItem(), props.fullGroups);
  },

  // called when item is dropped
  drop(props) {
    modelArr.push(props.model);
    return { model: modelArr };
  },
};

export default (
  ChildComponent,
  /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events, camelcase */
) =>
  @DropTarget('DRAG_RULE', cardTarget, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOver: monitor.isOver({ shallow: true }),
    canDrop: monitor.canDrop(),
    getDropResult: monitor.getDropResult(),
  }))
  @DragSource('DRAG_RULE', itemSource, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    connectDragPreview: connect.dragPreview(),
    dragItem: monitor.getItem(),
    canDrag: monitor.canDrag(),
    isDragging: monitor.isDragging(),
    getDropResult: monitor.getDropResult(),
  }))
  class RuleHOC extends Component {
    static propTypes = {
      count: PropTypes.number.isRequired,
      index: PropTypes.number,
      model: ruleType.isRequired,
      fullGroups: PropTypes.arrayOf(ruleType),
      dragItem: ruleType,
      isArchived: PropTypes.bool,
      isGrouped: PropTypes.bool,
      isSelected: PropTypes.bool.isRequired,
      toggleItemStatus: PropTypes.func,
      isPreSelected: PropTypes.bool.isRequired,
      reorderItem: PropTypes.func,
      selectGroupItem: PropTypes.func,
      partialUpdateItem: PropTypes.func,
      connectDragSource: PropTypes.func,
      reorder: PropTypes.node,
      isDragging: PropTypes.bool,
      isOver: PropTypes.bool,
      canDrop: PropTypes.bool,
      connectDragPreview: PropTypes.func,
      connectDropTarget: PropTypes.func,
    };

    state = {
      isReorderOpened: false,
    };

    componentDidMount() {
      if (this.props.connectDragPreview) {
        // Use empty image as a drag preview so browsers don't draw it
        this.props.connectDragPreview(getEmptyImage(), {
          captureDraggingState: false,
        });
      }
    }

    handleExpanded = exp => {
      expanded = exp;
    };

    handleOpenReorder = e => {
      e.stopPropagation();
      this.setState({
        isReorderOpened: true,
      });
    };

    handleCloseReorder = () => {
      this.setState({
        isReorderOpened: false,
      });
    };
    handleReorder = (e, action) => {
      e.stopPropagation();

      const { model, count, isGrouped, partialUpdateItem } = this.props;
      const { id, order, rule_group } = model;

      const group = !_isUndefined(rule_group) ? rule_group : null;
      const newId = _has(model, 'rule_group') && !isGrouped ? rule_group : id;

      if (action === 'up' && order > 0) {
        partialUpdateItem(newId, { order: order - 1 }, group, 'move');
      }

      if (action === 'down' && order < count - 1) {
        partialUpdateItem(newId, { order: order + 1 }, group, 'move');
      }
    };

    handleStatusChange = () => {
      const { model, toggleItemStatus } = this.props;
      const mode = !_has(model, 'rules') ? 'Rule' : 'Rule Group';
      const enabled =
        mode === 'Rule'
          ? !model.enabled
          : !model.rules.some(rule => rule.enabled === true);

      toggleItemStatus(
        model.id,
        model.name,
        {
          enabled,
        },
        mode,
        model.rule_group,
      );
    };

    handleOrderList = () => {
      const { index, count, isGrouped } = this.props;
      return _map(_range(count), val => ({
        order: isGrouped
          ? _toString(`${index + 1}.${val + 1}`)
          : _toString(val + 1),
        index: +val,
      }));
    };

    renderNumber() {
      const {
        model: { order },
        index,
        isGrouped,
      } = this.props;

      const number = (
        <div styleName="number rule">
          <Tooltip content="Move up" placement="right">
            <i
              className="icon-arrow-up"
              onClick={e => this.handleReorder(e, 'up')}
              data-test="order-up"
            />
          </Tooltip>
          <span onClick={this.handleOpenReorder} data-test="order">
            {!isGrouped ? order + 1 : `${index + 1}.${order + 1}`}
          </span>
          <Tooltip content="Move down" placement="right">
            <i
              className="icon-arrow-down"
              onClick={e => this.handleReorder(e, 'down')}
              data-test="order-down"
            />
          </Tooltip>
        </div>
      );

      if (isGrouped) {
        return <div styleName="number-wrap">{number}</div>;
      }
      return number;
    }

    renderReorderMenu() {
      const { isReorderOpened } = this.state;
      const { model, index, partialUpdateItem, isGrouped } = this.props;
      const newModel = !isGrouped
        ? { ...model, order: model.order + 1 }
        : { ...model, order: `${index + 1}.${model.order + 1}` };

      if (isReorderOpened) {
        return (
          <ReorderMenu
            isPortal
            model={newModel}
            index={model.order + 1}
            orderList={this.handleOrderList()}
            isOpen={isReorderOpened}
            reorderRef={this.reorder}
            onClose={this.handleCloseReorder}
            reorderItem={partialUpdateItem}
            isGrouped={isGrouped}
            mode="list"
          />
        );
      }
      return null;
    }

    render() {
      const {
        connectDragSource,
        connectDropTarget,
        isOver,
        canDrop,
        isDragging,
        dragItem,
        isArchived,
        isSelected,
      } = this.props;
      const number = this.renderNumber();

      return connectDragSource(
        connectDropTarget(
          <div>
            <ChildComponent
              {...this.props}
              isOver={isOver}
              canDrop={canDrop}
              isDragging={isDragging}
              onExpanded={this.handleExpanded}
              groupIsDragged={_has(dragItem, 'rules')}
              number={number}
              reorder={this.renderReorderMenu()}
              statusChange={this.handleStatusChange}
            />
            {this.props.isDragging && (
              <Preview
                number={number}
                isArchived={isArchived}
                isSelected={isSelected}
              />
            )}
          </div>,
        ),
      );
    }
  };
